import { useEffect, useMemo, useRef, useState } from "react";
import { adminListCollection, adminCreateCollection } from "@/lib/api";
import { X, Plus, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const slugify = (s) =>
  (s || "").toString().trim().toLowerCase()
    .replace(/[^a-z0-9\u0900-\u097F]+/g, "-")
    .replace(/(^-|-$)/g, "");

// Multi-select bound to a managed taxonomy collection (categories / deities / temples).
// `value` is an array of taxonomy slugs. New entries can be created inline.
export default function TaxonomyMultiSelect({ label, name, value = [], onChange, testid, hint }) {
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const boxRef = useRef(null);

  const reload = () =>
    adminListCollection(name)
      .then((rows) => setOptions(rows.map((r) => ({ slug: r.slug, label: r.name || r.title || r.slug, deva: r.deva_name || "" }))))
      .catch(() => setOptions([]));

  useEffect(() => { reload(); }, [name]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const byslug = useMemo(() => Object.fromEntries(options.map((o) => [o.slug, o])), [options]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter((o) => !q || o.label.toLowerCase().includes(q) || (o.deva || "").includes(query.trim()) || o.slug.includes(q));
  }, [options, query]);

  const exactExists = options.some((o) => o.label.toLowerCase() === query.trim().toLowerCase());

  const toggle = (slug) => {
    if (value.includes(slug)) onChange(value.filter((s) => s !== slug));
    else onChange([...value, slug]);
  };

  const createNew = async () => {
    const nm = query.trim();
    if (!nm) return;
    const slug = slugify(nm);
    if (!slug) return;
    if (byslug[slug]) { toggle(slug); setQuery(""); return; }
    setCreating(true);
    try {
      await adminCreateCollection(name, { name: nm, slug, order: options.length });
      await reload();
      if (!value.includes(slug)) onChange([...value, slug]);
      setQuery("");
      toast.success(`Added "${nm}"`);
    } catch (err) {
      toast.error("Could not add entry", { description: err.response?.data?.detail || err.message });
    } finally { setCreating(false); }
  };

  return (
    <div ref={boxRef} className="relative">
      <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">{label}</label>
      <div
        data-testid={testid}
        onClick={() => setOpen(true)}
        className="flex min-h-[42px] cursor-text flex-wrap items-center gap-1.5 border border-line bg-parchment px-2 py-1.5 focus-within:border-saffron"
      >
        {value.map((slug) => (
          <span key={slug} data-testid={`${testid}-chip-${slug}`} className="inline-flex items-center gap-1 rounded-full bg-[rgba(199,80,0,0.1)] px-2.5 py-1 text-xs text-saffron">
            {byslug[slug]?.label || slug}
            <button type="button" onClick={(e) => { e.stopPropagation(); toggle(slug); }} className="hover:text-[var(--saffron-dark)]"><X className="h-3 w-3" /></button>
          </span>
        ))}
        <input
          value={query}
          data-testid={`${testid}-input`}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (!exactExists && query.trim()) createNew(); else if (filtered[0]) { toggle(filtered[0].slug); setQuery(""); } } }}
          placeholder={value.length ? "" : "Search or add…"}
          className="min-w-[8rem] flex-1 bg-transparent px-1 py-0.5 text-sm text-ink focus:outline-none"
        />
        <ChevronDown className="h-4 w-4 shrink-0 text-ink-3" />
      </div>
      {hint && <p className="mt-1 text-xs text-ink-3">{hint}</p>}
      {open && (
        <div className="absolute z-30 mt-1 max-h-60 w-full overflow-y-auto border border-line bg-surface shadow-lg">
          {filtered.map((o) => (
            <button
              key={o.slug} type="button" data-testid={`${testid}-opt-${o.slug}`}
              onClick={() => toggle(o.slug)}
              className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-surface-alt ${value.includes(o.slug) ? "text-saffron" : "text-ink-2"}`}
            >
              <span>{o.label} {o.deva && <span className="font-deva text-ink-3">· {o.deva}</span>}</span>
              {value.includes(o.slug) && <span className="text-xs">✓</span>}
            </button>
          ))}
          {query.trim() && !exactExists && (
            <button type="button" data-testid={`${testid}-create`} onClick={createNew} disabled={creating}
              className="flex w-full items-center gap-1.5 border-t border-line px-3 py-2 text-left text-sm text-saffron hover:bg-surface-alt disabled:opacity-60">
              <Plus className="h-3.5 w-3.5" /> Create “{query.trim()}”
            </button>
          )}
          {filtered.length === 0 && !query.trim() && <p className="px-3 py-3 text-sm text-ink-3">No entries yet — type to add one.</p>}
        </div>
      )}
    </div>
  );
}
